import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus, Lock, Unlock, Printer, AlertTriangle, X } from 'lucide-react';
import { refertiProdottiAPI } from '../../services/refertiProdottiApi';
import { useAuthStore } from '../../store/useStore';
import '../../styles/RefertiScritti.css';
import '../../styles/RefertiScrittiConsole.css';

/**
 * RefertiScritti — i referti SCRITTI qui, nella scheda del paziente.
 *
 * È il posto dove un medico li cerca: non in una schermata a sé, ma
 * dentro la cartella della persona di cui sta parlando il referto. La
 * pagina dell'editor si apre da qui.
 *
 * Diversi dai referti RICEVUTI, che stanno sotto Documenti insieme agli
 * altri file caricati da fuori. Tenerli insieme farebbe sembrare che un
 * referto scritto dalla clinica e uno arrivato da un laboratorio siano
 * la stessa cosa, e non lo sono: di uno rispondiamo noi.
 */

const dataIta = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d) ? '—' : d.toLocaleDateString('it-IT');
};

const messaggio = (e, ripiego) => e?.response?.data?.message || e?.message || ripiego;

const RefertiScritti = ({ pazienteId }) => {
    const navigate = useNavigate();
    const utente = useAuthStore((s) => s.user);
    const [referti, setReferti] = useState([]);
    const [errore, setErrore] = useState(null);
    const [inCorso, setInCorso] = useState(false);

    const carica = useCallback(async () => {
        try {
            const r = await refertiProdottiAPI.elenco(pazienteId);
            setReferti(r.data.data.referti || []);
        } catch (e) {
            setErrore(messaggio(e, 'Non riesco a caricare i referti'));
        }
    }, [pazienteId]);

    useEffect(() => { carica(); }, [carica]);

    /* Scrive chi firma. La segreteria può crearne uno per conto di un
       medico dalla pagina del referto, ma qui il pulsante lo vede chi lo
       userà: nascondere un pulsante non è protezione — la regola la
       tengono le rotte — è evitare un clic che risponderebbe 400. */
    const puoScrivere = Boolean(utente?.medico_id);

    const nuovo = async () => {
        const titolo = window.prompt('Titolo del referto (per esempio: Valutazione fisiatrica)');
        if (!titolo || !titolo.trim()) return;
        try {
            setErrore(null);
            setInCorso(true);
            const r = await refertiProdottiAPI.crea({ paziente_id: pazienteId, titolo });
            navigate(`/referti/${r.data.data.referto.id}`);
        } catch (e) {
            setErrore(messaggio(e, 'Errore nella creazione del referto'));
        } finally {
            setInCorso(false);
        }
    };

    const stampa = async (r) => {
        try {
            const x = await refertiProdottiAPI.pdf(r.id);
            const url = window.URL.createObjectURL(new Blob([x.data], { type: 'application/pdf' }));
            window.open(url, '_blank');
            setTimeout(() => window.URL.revokeObjectURL(url), 30000);
        } catch (e) {
            setErrore(messaggio(e, 'Errore nella stampa'));
        }
    };

    return (
        <div className="rfs-riquadro">
            <header className="rfs-testa">
                <div>
                    <h2><FileText size={18} aria-hidden="true" /> Referti e ricette scritti qui</h2>
                    <p>
                        I referti prodotti dalla struttura. Quelli <b>ricevuti</b> da fuori
                        stanno sotto Documenti.
                    </p>
                </div>
                {puoScrivere && (
                    <button className="rfs-btn rfs-btn--principale" onClick={nuovo} disabled={inCorso}>
                        <Plus size={15} /> Scrivi un referto
                    </button>
                )}
            </header>

            {errore && (
                <p className="rfs-errore">
                    <AlertTriangle size={15} /> {errore}
                    <button onClick={() => setErrore(null)}><X size={14} /></button>
                </p>
            )}

            {referti.length === 0 ? (
                <p className="rfs-vuoto">
                    Nessun referto o ricetta scritti per questo paziente.
                </p>
            ) : (
                <table className="rfs-tabella">
                    <thead>
                        <tr><th>Tipo</th><th>Titolo</th><th>Data</th><th>Firma</th><th>Stato</th><th /></tr>
                    </thead>
                    <tbody>
                        {referti.map((r) => (
                            <tr key={r.id}>
                                <td className="rfs-sub">{r.tipo === 'ricetta' ? 'Ricetta' : 'Referto'}</td>
                                <td>
                                    <button className="rfs-collegamento"
                                            onClick={() => navigate(`/referti/${r.id}`)}>
                                        {r.titolo}
                                    </button>
                                    {r.versione > 1 && (
                                        <span className="rfs-versione">versione {r.versione}</span>
                                    )}
                                </td>
                                <td>{dataIta(r.data_referto)}</td>
                                <td className="rfs-sub">{r.medico_cognome} {r.medico_nome}</td>
                                <td>
                                    <span className={`rfs-stato ${r.stato}`}>
                                        {r.stato === 'bozza'
                                            ? <><Unlock size={12} /> Bozza</>
                                            : <><Lock size={12} /> Concluso</>}
                                    </span>
                                </td>
                                <td className="rfs-azioni">
                                    <button className="rfs-icona" title="Stampa"
                                            onClick={() => stampa(r)}>
                                        <Printer size={15} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default RefertiScritti;
